import { ArrowLeft, Sun, Moon } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useState, useEffect } from "react";
import { message } from "antd";
import welcomeBg from "../../assets/homebg.png";
import PreferencesService from "../../services/PreferencesService";
import { useQueryClient } from "@tanstack/react-query";

const PreferencesPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { theme, setTheme } = useTheme();

  // Unit preferences state
  const [weightUnit, setWeightUnit] = useState<string>("kg");
  const [heightUnit, setHeightUnit] = useState<string>("cm");
  const [language, setLanguage] = useState<string>("English");
  // const [timeFormat, setTimeFormat] = useState<string>("12"); // Commented out - Time Format card is disabled
  const [distanceUnit, setDistanceUnit] = useState<string>("km");
  const [temperatureUnit, setTemperatureUnit] = useState<string>("c");
  const [loading, setLoading] = useState<boolean>(false);

  // Load preferences from API on component mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await PreferencesService.getUserPreferences();
        setWeightUnit(prefs.weight_unit);
        setHeightUnit(prefs.height_unit);

        setDistanceUnit(prefs.distance_unit);
        setTemperatureUnit(prefs.temperature_unit);

        // Still load language from localStorage for now
        const savedLanguage =
          localStorage.getItem("glucera_language") || "English";
        // const savedTimeFormat =
        //   localStorage.getItem("glucera_time_format") || "12";

        setLanguage(savedLanguage);
        // setTimeFormat(savedTimeFormat); // Commented out - Time Format card is disabled
      } catch (error) {
        // Fallback to localStorage
        const savedWeightUnit =
          localStorage.getItem("glucera_weight_unit") || "kg";
        const savedHeightUnit =
          localStorage.getItem("glucera_height_unit") || "cm";
        const savedDistanceUnit =
          localStorage.getItem("glucera_distance_unit") || "km";
        const savedTemperatureUnit =
          localStorage.getItem("glucera_temperature_unit") || "c";

        setWeightUnit(savedWeightUnit);
        setHeightUnit(savedHeightUnit);
        setDistanceUnit(savedDistanceUnit);
        setTemperatureUnit(savedTemperatureUnit);
      }
    };

    loadPreferences();
  }, []);

  // Save preferences to API when changed
  const handleWeightUnitChange = async (unit: string) => {
    setLoading(true);
    try {
      const currentPrefs = await PreferencesService.getUserPreferences();

      let nextHeightUnit = heightUnit;
      let nextDistanceUnit = distanceUnit;
      let nextTemperatureUnit = temperatureUnit;

      // Auto-select other units based on weight unit (User Requirement)
      if (unit === "kg") {
        nextHeightUnit = "cm";
        nextDistanceUnit = "km";
        nextTemperatureUnit = "c";
      } else if (unit === "lbs") {
        nextHeightUnit = "ft_in";
        nextDistanceUnit = "mi";
        nextTemperatureUnit = "f";
      }

      const updatedPrefs = {
        ...currentPrefs,
        weight_unit: unit as "kg" | "lbs",
        height_unit: nextHeightUnit as "cm" | "ft_in",
        distance_unit: nextDistanceUnit as "km" | "mi",
        temperature_unit: nextTemperatureUnit as "c" | "f",
      };

      const success =
        await PreferencesService.updateUserPreferences(updatedPrefs);
      if (success) {
        setWeightUnit(unit);
        setHeightUnit(nextHeightUnit);
        setDistanceUnit(nextDistanceUnit);
        setTemperatureUnit(nextTemperatureUnit);

        localStorage.setItem("glucera_weight_unit", unit);
        localStorage.setItem("glucera_height_unit", nextHeightUnit);
        localStorage.setItem("glucera_distance_unit", nextDistanceUnit);
        localStorage.setItem("glucera_temperature_unit", nextTemperatureUnit);

        // Invalidate home page data to refresh BMI with new unit
        queryClient.invalidateQueries({ queryKey: ["homePageData"] });
      } else {
        message.error("Failed to update weight unit preference");
      }
    } catch (error) {
      message.error("Error updating preference");
    } finally {
      setLoading(false);
    }
  };

  const handleHeightUnitChange = async (unit: string) => {
    setLoading(true);
    try {
      const currentPrefs = await PreferencesService.getUserPreferences();
      const updatedPrefs = {
        ...currentPrefs,
        height_unit: unit as "cm" | "ft_in",
      };

      const success =
        await PreferencesService.updateUserPreferences(updatedPrefs);
      if (success) {
        setHeightUnit(unit);
        localStorage.setItem("glucera_height_unit", unit);

        // Invalidate home page data to refresh BMI with new unit
        queryClient.invalidateQueries({ queryKey: ["homePageData"] });

      } else {
        message.error("Failed to update height unit preference");
      }
    } catch (error) {
      message.error("Error updating preference");
    } finally {
      setLoading(false);
    }
  };

  const handleDistanceUnitChange = async (unit: string) => {
    setLoading(true);
    try {
      const currentPrefs = await PreferencesService.getUserPreferences();
      const updatedPrefs = {
        ...currentPrefs,
        distance_unit: unit as "km" | "mi",
      };

      const success =
        await PreferencesService.updateUserPreferences(updatedPrefs);
      if (success) {
        setDistanceUnit(unit);
        localStorage.setItem("glucera_distance_unit", unit);
      } else {
        message.error("Failed to update distance unit preference");
      }
    } catch (error) {
      message.error("Error updating preference");
    } finally {
      setLoading(false);
    }
  };

  const handleTemperatureUnitChange = async (unit: string) => {
    setLoading(true);
    try {
      const currentPrefs = await PreferencesService.getUserPreferences();
      const updatedPrefs = {
        ...currentPrefs,
        temperature_unit: unit as "c" | "f",
      };

      const success =
        await PreferencesService.updateUserPreferences(updatedPrefs);
      if (success) {
        setTemperatureUnit(unit);
        localStorage.setItem("glucera_temperature_unit", unit);
      } else {
        message.error("Failed to update temperature unit preference");
      }
    } catch (error) {
      message.error("Error updating preference");
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
    localStorage.setItem("glucera_language", lang);
  };

  // Commented out - Time Format card is disabled
  // const handleTimeFormatChange = (format: string) => {
  //   setTimeFormat(format);
  //   localStorage.setItem("glucera_time_format", format);
  // };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/glucera/profilepage")}
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">Settings</h1>
          <div className="w-10" />
        </div>

        {/* Theme Selection */}
        <Card className="rounded-2xl border-2 border-border bg-card p-5 shadow-lg">
          <h3 className="mb-4 text-lg font-bold text-foreground">Appearance</h3>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setTheme("light")}
              className={`rounded-xl border-2 p-4 transition-all ${theme === "light"
                ? "border-primary bg-primary/10 shadow-md"
                : "border-border hover:border-primary/50"
                }`}
            >
              <div className="flex flex-col items-center gap-2">
                <Sun className="h-7 w-7" />
                <span className="text-sm font-bold text-foreground">
                  Light Mode
                </span>
              </div>
            </button>
            <button
              onClick={() => setTheme("dark")}
              className={`rounded-xl border-2 p-4 transition-all ${theme === "dark"
                ? "border-primary bg-primary/10 shadow-md"
                : "border-border hover:border-primary/50"
                }`}
            >
              <div className="flex flex-col items-center gap-2">
                <Moon className="h-7 w-7" />
                <span className="text-sm font-bold text-foreground">
                  Dark Mode
                </span>
              </div>
            </button>
          </div>
        </Card>

        {/* Units */}
        <Card className="rounded-2xl border-2 border-border bg-card p-5 shadow-lg">
          <h3 className="mb-4 text-lg font-bold text-foreground">Units</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2">
              <span className="text-sm font-semibold text-foreground">
                Weight Unit
              </span>
              <select
                value={weightUnit}
                onChange={e => handleWeightUnitChange(e.target.value)}
                disabled={loading}
                className="rounded-lg border-2 border-border bg-background px-3 py-1.5 text-sm font-semibold text-foreground focus:border-primary focus:outline-none disabled:opacity-50"
              >
                <option value="kg">Kilograms (kg)</option>
                <option value="lbs">Pounds (lbs)</option>
              </select>
            </div>
            <div className="h-px bg-border" />
            <div className="flex items-center justify-between py-2">
              <span className="text-sm font-semibold text-foreground">
                Height Unit
              </span>
              <select
                value={heightUnit}
                onChange={e => handleHeightUnitChange(e.target.value)}
                disabled={true}
                className="rounded-lg border-2 border-border bg-background px-3 py-1.5 text-sm font-semibold text-foreground focus:border-primary focus:outline-none disabled:opacity-50 cursor-not-allowed"
              >
                <option value="cm">Centimeters (cm)</option>
                <option value="ft_in">Ft-Inch</option>
              </select>
            </div>
            <div className="h-px bg-border" />
            <div className="flex items-center justify-between py-2">
              <span className="text-sm font-semibold text-foreground">
                Distance Unit
              </span>
              <select
                value={distanceUnit}
                onChange={e => handleDistanceUnitChange(e.target.value)}
                disabled={true}
                className="rounded-lg border-2 border-border bg-background px-3 py-1.5 text-sm font-semibold text-foreground focus:border-primary focus:outline-none disabled:opacity-50 cursor-not-allowed"
              >
                <option value="km">Kilometers (km)</option>
                <option value="mi">Miles (mi)</option>
              </select>
            </div>
            <div className="h-px bg-border" />
            <div className="flex items-center justify-between py-2">
              <span className="text-sm font-semibold text-foreground">
                Temperature Unit
              </span>
              <select
                value={temperatureUnit}
                onChange={e => handleTemperatureUnitChange(e.target.value)}
                disabled={true}
                className="rounded-lg border-2 border-border bg-background px-3 py-1.5 text-sm font-semibold text-foreground focus:border-primary focus:outline-none disabled:opacity-50 cursor-not-allowed"
              >
                <option value="c">Celsius (°C)</option>
                <option value="f">Fahrenheit (°F)</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Language */}
        <Card className="rounded-2xl border-2 border-border bg-card p-5 shadow-lg">
          <h3 className="mb-4 text-lg font-bold text-foreground">Language</h3>
          <select
            value={language}
            onChange={e => handleLanguageChange(e.target.value)}
            className="w-full rounded-lg border-2 border-border bg-background px-3 py-2 text-sm font-semibold text-foreground focus:border-primary focus:outline-none"
          >
            <option value="English">English</option>
            <option value="Spanish">Spanish</option>
            <option value="French">French</option>
            <option value="German">German</option>
            <option value="Hindi">Hindi</option>
          </select>
        </Card>

        {/* Time Format */}
        {/* <Card className="rounded-2xl border-2 border-border bg-card p-5 shadow-lg">
          <h3 className="mb-4 text-lg font-bold text-foreground">
            Time Format
          </h3>
          <select
            value={timeFormat}
            onChange={e => handleTimeFormatChange(e.target.value)}
            className="w-full rounded-lg border-2 border-border bg-background px-3 py-2 text-sm font-semibold text-foreground focus:border-primary focus:outline-none"
          >
            <option value="12">12-hour (AM/PM)</option>
            <option value="24">24-hour</option>
          </select>
        </Card> */}
      </div>
    </div>
  );
};

export default PreferencesPage;
